import AccordionCustom from "@components/Accordion/AccordionCustom";
import RelatedCaseStudies from "@components/CaseStudyComponents/RelatedCaseStudies";
import GenerateLinksHeader from "@components/LandingPageComponents/GenerateLinksHeader";
import QrGenerator from "@components/LandingPageComponents/QrGenerator";
import NewLayout from "@components/new-ui/Layout";
import TrustGallabox from "@components/TrustGallabox";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import link from "public/link-connector.png";
import icon1 from "public/link-icon-1.png";
import icon2 from "public/link-icon-2.png";
import icon3 from "public/link-icon-3.png";
import React, { useRef } from "react";
import schema from "@components/faqSchema/whatsapp-qr-code-generator-faqSchema.json";
const scriptText = JSON.stringify(schema, null, 2);

const RELATED_CASE_STUDIES = [
    {
        id: 2,
        bannerimg: "https://assets.gallabox.com/gb-home/hugg/hugg-og.png",
        tag: "",
        title: "Maximizing Conversions and Streamlining Support: Hugg's Transformation with Gallabox",
        link: "/case-study/hugg",
        description: "Here's the awe-inspiring tale of how Hugg boosted efficiency, enhanced customer support, and multiplied conversion rates with Gallabox.",
    },
    {
        id: 1,
        bannerimg: "https://assets.gallabox.com/gb-home/qatar-insurance-company/qatar-og.png",
        tag: "",
        title: "How Qatar Insurance Company Doubled Lead Generation with Gallabox",
        link: "/case-study/qatar-insurance-company",
        description: "Here's the awe-inspiring tale of how Qatar Insurance Company boosted lead generation, customer satisfaction, and automated responses with Gallabox.",
    },
];

const faq = [
    {
        question: "What is the QR code for WhatsApp business?",
        answer: "A WhatsApp QR code is a unique code that opens a direct communication channel through a company's WhatsApp Business number.",
    },
    {
        question: "What happens when someone scans the WhatsApp QR Code?",
        answer: "When a WhatsApp QR Code is scanned with a smartphone camera or a QR code reader app, it automatically opens a chat with the assigned WhatsApp business account, simplifying connecting with the business.",
    },
    {
        question: "Are there any costs involved in creating a WhatsApp QR Code?",
        answer: "Absolutely not; creating a WhatsApp QR code with Gallabox is entirely free of charge",
    },
    {
        question: "Does Gallabox impose any restrictions on generating a WhatsApp QR Code?",
        answer: "Not at all; there are no limits when utilising Gallabox to create a WhatsApp QR Code.",
    },
    {
        question: "What advantages do SMB owners gain from generating a WhatsApp QR Code?",
        answer: "Using WhatsApp QR Codes, SMB owners can enhance customer engagement, boost sales, and simplify their communication practices with customers",
    },
    {
        question: "Can you distinguish between a WhatsApp link and a WhatsApp QR code?",
        answer: "A WhatsApp link serves as a direct pathway to your WhatsApp Business number. Conversely, a WhatsApp QR code is a scannable asset granting access to your WhatsApp Business number.",
    },
];

const WhatsappQR: React.FC = () => {
    const shortUrlRef = useRef<HTMLDivElement>(null);

    const handleScrollToShortUrl = () => {
        if (shortUrlRef && shortUrlRef.current) {
            shortUrlRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <NewLayout
            title="WhatsApp QR Code Generator — Create Free Chat QR Codes"
            description="Generate WhatsApp QR Codes instantly with our free WhatsApp QR Code Generator. Let customers chat with a simple scan — no coding needed!"
            canonical="https://gallabox.com/whatsapp-qr-code-generator"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-qr-generator/qr-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-qr-code-generator-image",
                heroOrOgImageDescription: "WhatsApp QR Code Generator: Create Instant Chat QR Codes for Free",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/whatsapp-qr-generator/qr-header.png",
                softwareFeatureName: "whatsapp-qr-code-generator",
                faq: faq,
            }}
        >
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: scriptText }} />
            </Head>
            <GenerateLinksHeader
                pageTitle="WhatsApp QR Code Generator "
                mainTitle="WhatsApp QR Code Generator: Create Instant Chat QR Codes for Free"
                description="Make engagement problems a thing of the past with a WhatsApp QR Code Generator. Let your customers (current and potential) reach you effortlessly, capturing every possible opportunity."
                imageUrl="https://assets.gallabox.com/gb-home/whatsapp-qr-generator/qr-header.png"
                scrollHandler={handleScrollToShortUrl}
                noSection
            />

            <div className=" mx-auto flex lg:flex-row flex-col w-full pb-8 pt-8" style={{ maxWidth: "1200px" }}>
                <div className="lg:order-2 order-1 lg:w-1/2 w-full lg:text-left text-center lg:px-0 px-2">
                    <h2 className="lg:text-3xl text-xl  font-bold pb-4  ">Why Should I Use a WhatsApp QR Code Generator?</h2>
                    <div className="">
                        <p className="lg:text-lg text-base text-gray-600 pb-4">
                            With a WhatsApp QR code generator, you can address issues such as overlooked opportunities, diminishing sales, and disconnected contacts, seamlessly
                            connecting you with customers and prospects.
                        </p>
                        <div>
                            {" "}
                            <h3 className="lg:text-2xl text-xl  font-bold pb-4 ">Engage Audiences Swiftly</h3>
                            <p className="lg:text-lg text-base  text-gray-600 pb-4 ">
                                With just a few clicks, set up a direct QR code that enables individuals without your contact details to initiate communication via WhatsApp
                                Business.
                            </p>
                        </div>
                        <div>
                            {" "}
                            <h3 className="lg:text-2xl text-xl  font-bold pb-4 ">Improve Your Business Processes</h3>
                            <p className="lg:text-lg text-base  text-gray-600 pb-4 ">
                                Improve your communication strategies, boost engagement, and enrich customer experience by allowing customers and prospective clients to reach you
                                instantly.
                            </p>
                        </div>
                        <div>
                            {" "}
                            <h3 className="lg:text-2xl text-xl  font-bold pb-4 ">Unlock Revenue Growth</h3>
                            <p className="lg:text-lg text-base text-gray-600 pb-4 ">
                                Access untapped revenue potentials, boost your sales, and heighten your profit margins by leveraging the power of personalised WhatsApp QR codes.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="lg:order-1 order-2 lg:w-1/2 w-full p-10">
                    <img src="https://assets.gallabox.com/gb-home/whatsapp-qr-generator/qr-section1.png" alt="" />
                </div>
            </div>

            <div className="flex flex-col items-center justify-center py-10" style={{ backgroundColor: "#F0FFF4" }}>
                <div className="lg:text-center text-left mb-8">
                    <h2 className="text-3xl font-bold lg:text-center text-left  ">How to Create QR Codes for WhatsApp Business in 3 Simple Steps?</h2>
                    <p className="text-lg lg:text-center text-left px-5">In just three simple steps, you can create a personalised WhatsApp QR code through Gallabox:</p>
                </div>
                <img src={link} className="lg:block hidden max-w-2xl mx-auto" alt="Steps to create QR code"></img>
                <div className="flex flex-col lg:flex-row justify-center lg:space-x-4">
                    <div className="w-80 h-40 border border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <img src={icon1} alt="Image 1" className=" lg:hidden block w-12 h-12 object-cover rounded-t-lg" />
                        <h2 className="text-lg font-bold my-2">Provide Your Details</h2>
                        <p className="text-sm text-center">Just feed your WhatsApp Business number into Gallabox&apos;s WhatsApp QR code generator</p>
                    </div>
                    <div className="w-80 h-40 border border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <img src={icon2} alt="Image 2" className=" lg:hidden block w-12 h-12 object-cover rounded-t-lg" />
                        <h2 className="text-lg font-bold my-2">Craft a Unique Message</h2>
                        <p className="text-sm text-center">Add an alluring and engaging message to your WhatsApp code to make it more interactive</p>
                    </div>
                    <div className="w-80 h-40 border border-gray-300 rounded-lg flex flex-col items-center justify-center">
                        <img src={icon3} alt="Image 3" className=" lg:hidden block w-12 h-12 object-cover rounded-t-lg" />
                        <h2 className="text-lg font-bold my-2">Generate a WhatsApp QR Code</h2>
                        <p className="text-sm text-center">Once ready, hit the &quot;Generate Link&quot; button to get your unique, free WhatsApp QR code</p>
                    </div>
                </div>
            </div>
            <div ref={shortUrlRef}>
                <QrGenerator />
            </div>
            <TrustGallabox />

            <RelatedCaseStudies
                sectionTitle={<h3 className="text-4xl pb-6">Hear Our Customers&apos; Experiences</h3>}
                sectionDescription={
                    <p className="text-xl font-normal text-center text-gray-600">
                        <b>Trusted globally by over</b> <b className=" text-blue-500">1500 companies across more than 40 countries</b> , Gallabox is the go-to solution for managing
                        and automating WhatsApp business communication.
                    </p>
                }
                related={RELATED_CASE_STUDIES}
            />

            <YellowCTAhref
                buttonText="Start for Free"
                customText={
                    <h3 className="text-2xl md:text-3xl font-bold text-yellow-800">
                        Drive Quality Leads with QR Codes; Boost <br /> Conversions Using WhatsApp Automation!
                    </h3>
                }
            />

            <section className="max-w-6xl py-4 mx-auto px-4">
                <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                    <h2 className="lg:text-4xl text-3xl text-center font-bold text-gray-700">
                        WhatsApp QR Code Generator:
                        <br /> Frequently Asked Questions
                    </h2>
                </div>
                <AccordionCustom title="WhatsApp QR Code Generator" items={faq} />
            </section>
        </NewLayout>
    );
};

export default WhatsappQR;
